import { useKeycloak } from "react-keycloak";
import React, { useEffect, useState } from "react";
import {
    useAssignTenantMutation, useDeleteUnusedStorageConfigsMutation,
    useGetMeLazyQuery,
    UserDto,
    useResetTenantMutation,
    useUpdateTenantMutation
} from "../../api/models";
import { createBaseContext } from "../../services/apiService";
import { get as _get } from "lodash";
import { FormFeedback, FormGenerator } from "../../utils/formGenerator";
import { UserSettingsFormConfig } from "./forms/userSettingsFormConfig";
import { FormControlLabel, Switch } from "@material-ui/core";
import { Card, CardBody, CardHeader, FbButton, FbTab, FbTabs } from "@fabricks/ui-components";
import { Skeleton } from "@material-ui/lab";
import { FormikHelpers } from "formik/dist/types";
import { FormikValues } from "formik";
import { extractError, handleResponseWithSwal } from "../../utils/apiUtils";
import { showSuccess } from "../../utils/sweetAlertHelper";
import { hasRole, isAdmin } from "../../services/developerAccessService";

export const UserSettings = () => {
    const [keycloak, keycloakInitialized] = useKeycloak()
    const [isDataLoading, setDataLoading] = useState(true)
    const [activeTab, setActiveTab] = useState(0)
    const [assignTenantRequest] = useAssignTenantMutation()
    const [meQueryRequest, meRequestResponse] = useGetMeLazyQuery(createBaseContext(keycloak.token || ''))
    const [executeTenantResetRequest, executeTenantResetResult] = useResetTenantMutation()
    const [executeDeleteUnusedStorageConfigRequest, executeDeleteUnusedStorageConfigResult] = useDeleteUnusedStorageConfigsMutation()
    const [formInitialValues, setFormInitialValues] = useState({
        name: '',
        surname: '',
        department: '',
        email: '',
        username: '',
        tenant: '',
    })
    const isDarkMode = localStorage.getItem('viewMode') === 'dark'
    const assignedRoles = _get(keycloak, 'tokenParsed.resource_access', {})

    useEffect(() => {
        if (keycloakInitialized) {
            if (!keycloak.authenticated || keycloak.isTokenExpired()) {
                alert("NOT AUTH")
            }
            if (keycloak.token) {
                meQueryRequest()
            }

            return () => {
            }
        }
    }, [])

    useEffect(() => {
        if (meRequestResponse.data) {
            const { name, surname, department, email, username, tenant } = _get(
                meRequestResponse,
                'data.getMe.data.0',
                {}
            ) as UserDto
            setFormInitialValues({
                name: name || '',
                surname: surname || '',
                department: department || '',
                email: email || '',
                username: username || '',
                tenant: _get(tenant, 'uuid', '') as string,
            })
        }
        return () => {
        }
    }, [meRequestResponse])

    useEffect(() => {
        if (formInitialValues.name.length > 0) {
            setTimeout(() => {
                setDataLoading(false)
            }, 0)
        }
    }, [formInitialValues])

    if (meRequestResponse.loading || isDataLoading) {
        return (
            <>
                <Skeleton variant="text" animation="wave" width="20%" />
                <Skeleton variant="rect" animation="wave" height="40px" />
                <Skeleton variant="text" animation="wave" width="20%" />
                <Skeleton variant="rect" animation="wave" height="40px" />
                <Skeleton variant="text" animation="wave" width="20%" />
                <Skeleton variant="rect" animation="wave" height="40px" />
                <Skeleton variant="text" animation="wave" width="20%" />
                <Skeleton variant="rect" animation="wave" height="40px" />
                <Skeleton variant="text" animation="wave" width="20%" />
                <Skeleton variant="rect" animation="wave" height="40px" />
            </>
        )
    }

    const toggleViewMode = () => {
        if (localStorage.getItem('viewMode') === 'dark') {
            localStorage.setItem('viewMode', '')
        } else {
            localStorage.setItem('viewMode', 'dark')
        }
        window.location.reload()
    }

    const onSubmit = (
        values: any,
        actions: FormikHelpers<FormikValues>,
        setFeedback: React.Dispatch<React.SetStateAction<FormFeedback>>
    ): void => {
        const userUuid = _get(meRequestResponse, 'data.getMe.data.0.uuid', null)
        const params = createBaseContext(keycloak.token || '', {
            userId: userUuid,
            tenantId: values.tenant,
        })

        const submitForm = (): void => {
            assignTenantRequest(params)
                .then((response: any) => {
                    const error = extractError(response, 'assignTenant')
                    if (error) {
                        return setFeedback({
                            type: 'error',
                            message: `${_get(error, 'message', 'unknown')} (${_get(error, 'code', 'XXX')})`,
                        })
                    }
                    setFeedback({ type: 'success', message: 'Successfully switched the tenant', timeout: 2500 })
                })
                .catch((err: any) => {
                    setFeedback({ type: 'error', message: err })
                })
                .finally(() => {
                    actions.setSubmitting(false)
                })
        }
        setTimeout(submitForm, 1500)
    }

    const executeTenantReset = () => {
        executeTenantResetRequest({
            context: { headers: { Authorization: `Bearer ${keycloak.token || 'unknown'}` } },
            variables: {},
        }).then(handleResponseWithSwal('resetTenant', () => showSuccess('Reset tenant was successfull', 'Success')))
    }
    const deleteUnusedStorageConfigs = () => {
        executeDeleteUnusedStorageConfigRequest({
            context: { headers: { Authorization: `Bearer ${keycloak.token || 'unknown'}` } },
            variables: {},
        }).then(handleResponseWithSwal("deleteUnusedStorageConfigs", () => showSuccess('Deleting unused storage configs was successfull', 'Success')))
    }

    const renderTab = () => {
        switch (activeTab) {
            case 0: {
                // eslint-disable-next-line react/jsx-no-undef
                return (
                    <>
                        <FormGenerator
                            id="profile-form"
                            initialValues={formInitialValues}
                            config={UserSettingsFormConfig(assignedRoles)}
                            onSubmit={onSubmit}
                            submitLabel="Save"
                        />
                        <FormControlLabel
                            control={
                                <Switch checked={isDarkMode} onChange={toggleViewMode} value="hidden" color="primary" />
                            }
                            label="DarkMode"
                        />
                    </>
                )
            }
            case 1: {
                if (!isAdmin(assignedRoles)) {
                    return <p>You do not have enough rights</p>
                }
                return (
                    <>
                    <FbButton variant="contained" color="primary" fullWidth={true} style={{marginBottom: "15px"}}
                              onClick={executeTenantReset}>
                        Reset tenant
                    </FbButton>
                        <FbButton variant="contained" color="primary" fullWidth={true} style={{marginBottom: "15px"}}
                                  onClick={deleteUnusedStorageConfigs}>
                            Delete unused storage configs
                        </FbButton>
                    </>
                )
            }
            default: {
                return null
            }
        }
    }
    return (
        <Card className="card">
            <CardHeader
                className="coloredHeader" color="primary" isStats={false} type="generic">
                <FbTabs centered activeTabIndex={activeTab} didChangeTab={setActiveTab}>
                    <FbTab label="Profile" value={0} />
                    <FbTab label="Tenant" value={1} />
                </FbTabs>
            </CardHeader>
            <CardBody align="center">{renderTab()}</CardBody>
        </Card>
    )
}
